import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';

export type HealthStatus = 'ok' | 'degraded' | 'down' | 'unknown';

export interface HealthCheck {
  status: 'ok' | 'fail';
  latency_ms: number;
  required: boolean;
  detail?: string;
}

export interface HealthTarget {
  target_kind: 'SERVICE' | 'TENANT';
  target_key: string;
  status: HealthStatus;
  latency_ms: number | null;
  checks: Record<string, HealthCheck>;
  version: string | null;
  git_sha: string | null;
  sampled_at: string | null;
}

export interface PlatformHealth {
  services: HealthTarget[];
  tenants: HealthTarget[];
  generated_at: string;
  live: boolean;
  checkup_enabled?: boolean;
}

export interface PlatformHealthCheckupSettings {
  checkup_enabled: boolean;
  updated_at: string | null;
  updated_by: number | null;
}

export interface UptimeRow {
  target_kind: 'SERVICE' | 'TENANT';
  target_key: string;
  uptime: number | null;
  samples: number;
}

export interface HealthHistoryPoint {
  bucket_start: string;
  status: HealthStatus;
  samples: number;
}

export interface HealthHistory {
  target: string;
  bucket: string;
  points: HealthHistoryPoint[];
}

export interface HealthUptime {
  window: string;
  from: string;
  to: string;
  rows: UptimeRow[];
}

@Injectable({ providedIn: 'root' })
export class PlatformHealthService {
  private readonly base = `${environment.apiBaseUrl.replace(/\/$/, '')}/platform/health`;

  constructor(private readonly http: HttpClient) {}

  current(live = false): Observable<ApiEnvelope<PlatformHealth>> {
    // Only send `live` when true — a fresh probe is opt-in, the default reads the latest samples.
    const params = live ? new HttpParams().set('live', 'true') : new HttpParams();
    return this.http.get<ApiEnvelope<PlatformHealth>>(this.base, { params });
  }

  history(
    target: string,
    from: string,
    to: string,
    bucket: '5m' | '1h' | '1d',
  ): Observable<ApiEnvelope<HealthHistory>> {
    const params = new HttpParams().set('target', target).set('from', from).set('to', to).set('bucket', bucket);
    return this.http.get<ApiEnvelope<HealthHistory>>(`${this.base}/history`, { params });
  }

  uptime(window: '24h' | '7d' | '30d' | '90d'): Observable<ApiEnvelope<HealthUptime>> {
    const params = new HttpParams().set('window', window);
    return this.http.get<ApiEnvelope<HealthUptime>>(`${this.base}/uptime`, { params });
  }

  getCheckupSettings(): Observable<ApiEnvelope<PlatformHealthCheckupSettings>> {
    return this.http.get<ApiEnvelope<PlatformHealthCheckupSettings>>(`${this.base}/settings`);
  }

  updateCheckupSettings(checkup_enabled: boolean): Observable<ApiEnvelope<PlatformHealthCheckupSettings>> {
    return this.http.patch<ApiEnvelope<PlatformHealthCheckupSettings>>(`${this.base}/settings`, {
      checkup_enabled,
    });
  }
}
